import * as React from 'react';
import * as styles from './OrganizationalAssetPanel.css';
import { IOrgAssetsInfo } from '../../../interfaces/IOrgAssetsInfo';
import { IEnvironment } from '../../../interfaces/IEnvironment';
import UploaderService from '../../../services/UploaderService';
import { IAPanel } from '../../panels/Panel';
import { LocalizationService } from '../../../services/LocalizationService';
import { IAOrgAssetFolder } from './orgAssetFolder/OrgAssetFolder';
import { IOrgAssetGraphItem } from '../../../interfaces/OrgAssets/IOrgAssetGraphItem';
import OrgAssetsService from '../../../services/OrgAssetsService';
import { IAOrgAssetSubFolder } from './orgAssetFolder/OrgAssetSubFolder';
import { IAOrgAssetFile } from './orgAssetFile/OrgAssetFile';
import { IOrgAssetBreadCrumbPath } from '../../../interfaces/OrgAssets/IOrgAssetBreadCrumbPath';
import { IAOrgAssetBreadCrumb } from './orgAssetBreadCrumb/OrgAssetBreadCrumb';
import { IASkeleton } from '../../skeleton/Skeleton';

export interface IAOrgAssetPanelProps {
  open: boolean;
  token: string;
  tenant: string;
  environment: IEnvironment;
  highlightColor: string;
  fileTypeToDisplay?: "image" | "video";
  isMobile?: boolean;
  marginTop?: number;
  close: () => void;
  onSelect: (image: string, lastModified?: string) => void;
}

export interface IAOrgAssetPanelState {
  isLoading: boolean;
  hasErrorLoading: boolean;
  orgAssets: IOrgAssetsInfo[];
  orgAssetLoaded: IOrgAssetsInfo;
  filesAndFolders: IOrgAssetGraphItem[];
  breadCrumbPath: IOrgAssetBreadCrumbPath[];
}

export class IAOrganizationalAssetPanel extends React.Component<IAOrgAssetPanelProps, IAOrgAssetPanelState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: IAOrgAssetPanelProps) {
    super(props);
    this.state = {
      isLoading: false,
      hasErrorLoading: false,
      orgAssets: undefined,
      orgAssetLoaded: undefined,
      filesAndFolders: undefined,
      breadCrumbPath: undefined
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    if (this.props.open) {
      this.loadOrgAssetsMapping();
    }
  }

  public componentDidUpdate(prevProps: Readonly<IAOrgAssetPanelProps>): void {
    if (prevProps.open !== this.props.open && this.props.open) {
      this.loadOrgAssetsMapping();
    }
  }

  private loadOrgAssetsMapping() {
    this.setState({ isLoading: true });
    UploaderService.getOrgAssets(this.props.environment, this.props.token, this.props.tenant)
    .then((orgAssets) => this.setState({ orgAssets }))
    .catch(() => this.setState({ hasErrorLoading: true }))
    .finally(() => this.setState({ isLoading: false }));
  }

  private loadTopLevelFolderContent(orgAsset: IOrgAssetsInfo){
    this.setState({
      isLoading: true,
      orgAssetLoaded: orgAsset,
      breadCrumbPath: [{ name: orgAsset.title }],
      filesAndFolders: []
    }, () => {
      const pathName = orgAsset.settings?.libraryUrl?.decodedUrl?.slice(orgAsset.settings?.libraryUrl?.decodedUrl?.lastIndexOf("/") + 1);
      OrgAssetsService.getFilesAndFoldersUsingGraph(this.props.environment, this.props.tenant, orgAsset.site?.replace("/sites/", ""), pathName)
        .then(items => this.setFilesAndFolders(items))
        .finally(() => this.setState({ isLoading: false }));
    });

  }

  private loadSubFolderContent(subFolderId: string, breadCrumbPath: IOrgAssetBreadCrumbPath[]){
    const pathName = this.state.orgAssetLoaded.settings?.libraryUrl?.decodedUrl?.slice(this.state.orgAssetLoaded.settings?.libraryUrl?.decodedUrl?.lastIndexOf("/") + 1);
    this.setState({isLoading: true, breadCrumbPath: breadCrumbPath, orgAssetLoaded: this.state.orgAssetLoaded, filesAndFolders: this.state.filesAndFolders}, () => {
      OrgAssetsService.getSubFolderFilesAndFoldersUsingGraph(this.props.environment, this.props.tenant, this.state.orgAssetLoaded.site?.replace("/sites/", ""), pathName, subFolderId)
        .then(items => this.setFilesAndFolders(items))
        .finally(() => this.setState({ isLoading: false }));
    });
  }

  private setFilesAndFolders(items: IOrgAssetGraphItem[]) {
    // Save folders and files of the correct mime type
    this.setState({filesAndFolders: items.filter(item => item.childCount !== null || (item.mimeType && item.mimeType.includes(this.props.fileTypeToDisplay)))});
  }

  private resetState(){
    this.setState({
      isLoading: false,
      hasErrorLoading: false,
      orgAssets: undefined,
      orgAssetLoaded: undefined,
      filesAndFolders: undefined,
      breadCrumbPath: undefined
    });
  }

  public render(): JSX.Element {
    return (
      <div id="IA_OrgAssetsUploader">
        <IAPanel
          open={this.props.open}
          transition={"slide"}
          showNavigationBar={true}
          marginTop={this.props.marginTop}
          isMobile={this.props.isMobile}
          width={this.props.isMobile ? window.innerWidth : 1000}
          close={() => {
            this.props.close();
            this.resetState();
          }}
        >
          <div className={styles.IA_OrgAssetContainer}>
            {this.state.hasErrorLoading
            ?
              <p className={styles.IA_OrgAssetErrorMessage}>{this.localizationService.strings.Uploader_OrgAssetLoadingError}</p>
            :
            <>
              <h1>
                {this.props.fileTypeToDisplay === "video" ? this.localizationService.strings.Uploader_OurOrganization_Header_video : this.localizationService.strings.Uploader_OurOrganization_Header}
              </h1>
              {this.state.breadCrumbPath &&
                <IAOrgAssetBreadCrumb
                  isLoading={this.state.isLoading}
                  breadCrumbPath={this.state.breadCrumbPath}
                  onClickTopLevel={() => {
                    if (this.state.isLoading) return;
                    this.setState({
                      breadCrumbPath: undefined,
                      filesAndFolders: undefined,
                      orgAssetLoaded: undefined
                    });
                  }}
                  onClickFolder={breadCrumb => {
                    if (this.state.isLoading) return;
                    const breadCrumbPath = [...this.state.breadCrumbPath];
                    const index = breadCrumbPath.findIndex(i => i.id === breadCrumb.id);
                    breadCrumbPath.length = index + 1;
                    if (breadCrumb.id) {
                      this.loadSubFolderContent(breadCrumb.id, breadCrumbPath);
                    } else {
                      this.loadTopLevelFolderContent(this.state.orgAssetLoaded);
                    }
                  }}
                />
              }
              <div className={styles.IA_OrgAssetItemsContainer}>
                {this.state.isLoading ?
                  <>
                    {(Array.from(Array(5).keys())).map(i => {
                      const width = i > 1 && this.state.orgAssetLoaded ? 355 : 200;
                      const height = 200;
                      return <IASkeleton
                        key={i}
                        skeleton={{
                          height,
                          width,
                          marginBottom: 15,
                          marginRight: 15,
                          borderRadius: 5
                        }}
                      />
                    })}
                  </>
                :
                  <>
                    {this.state.filesAndFolders ? 
                      <>
                        {this.state.filesAndFolders.map(element => {
                          if (element.childCount !== null) {
                            return <IAOrgAssetSubFolder
                              folder={element}
                              highlightColor={this.props.highlightColor}
                              onClick={() => {
                                const breadCrumbPath = [...this.state.breadCrumbPath];
                                breadCrumbPath.push({ name: element.name, id: element.id });
                                this.loadSubFolderContent(element.id, breadCrumbPath);
                              }}
                            />
                          }
                          return <IAOrgAssetFile
                            file={element}
                            environment={this.props.environment}
                            tenant={this.props.tenant}
                            token={this.props.token}
                            onClick={() => {
                              this.props.onSelect(element.webUrl);
                              this.resetState();
                              this.props.close();
                            }}
                          />
                        })}
                        {this.state.filesAndFolders.length === 0 && <p>{this.localizationService.strings.Uploader_OurOrganization_EmptyFolder}</p>}
                      </>
                    : 
                      <>
                        {this.state.orgAssets?.map(orgAsset => {
                          return <IAOrgAssetFolder
                            key={orgAsset.id}
                            name={orgAsset.title}
                            thumbnail={orgAsset.settings?.thumbnailUrl?.decodedUrl}
                            environment={this.props.environment}
                            tenant={this.props.tenant}
                            token={this.props.token}
                            onClick={() => this.loadTopLevelFolderContent(orgAsset)}
                          />
                        })}
                      </>
                    }
                  </>
                }
              </div>
            </>}
          </div>
        </IAPanel>
      </div>
    );
  }
}
